import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export function BreathingGlowLoader({ onComplete }) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Only show loader once per session
    if (sessionStorage.getItem('sendpi_loader_seen')) {
      setVisible(false);
      onComplete?.();
      return;
    }

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    const timer = setTimeout(() => {
      setVisible(false);
      document.body.style.overflow = '';
      sessionStorage.setItem('sendpi_loader_seen', '1');
      setTimeout(() => onComplete?.(), 600);
    }, 2800);

    return () => {
      clearTimeout(timer);
      document.body.style.overflow = '';
    };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#030303]"
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          data-testid="page-loader"
        >
          {/* Breathing glow orbs */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Violet orb */}
            <motion.div
              className="absolute top-1/2 left-1/2 w-[500px] h-[500px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(138,43,226,0.25) 0%, rgba(138,43,226,0) 70%)',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.4, 0.8, 0.4],
              }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
            {/* Cyan orb */}
            <motion.div
              className="absolute top-1/2 left-1/2 w-[400px] h-[400px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(0,229,255,0.2) 0%, rgba(0,229,255,0) 70%)',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                scale: [1.3, 1, 1.3],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 2.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.3,
              }}
            />
            {/* Blue accent orb */}
            <motion.div
              className="absolute top-1/2 left-1/2 w-[300px] h-[300px] rounded-full"
              style={{
                background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(59,130,246,0) 70%)',
                transform: 'translate(-50%, -50%)',
              }}
              animate={{
                scale: [1, 1.6, 1],
                opacity: [0.5, 0.2, 0.5],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.6,
              }}
            />
          </div>

          {/* Logo and text */}
          <div className="relative z-10 flex flex-col items-center gap-6">
            <motion.img
              src="/sendpi-logo.png"
              alt="SendPI"
              className="h-20 w-auto"
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              style={{
                filter: 'drop-shadow(0 0 30px rgba(138,43,226,0.4)) drop-shadow(0 0 60px rgba(0,229,255,0.2))',
                mixBlendMode: 'normal',
              }}
            />
            <motion.div
              className="flex items-center gap-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <span className="font-outfit text-2xl font-light tracking-tight text-white/80">
                Send
              </span>
              <span className="font-outfit text-2xl font-bold tracking-tight pi-gradient-text glow-text">
                PI
              </span>
            </motion.div>

            {/* Subtle loading bar */}
            <motion.div
              className="w-16 h-[2px] rounded-full overflow-hidden bg-white/[0.06]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ background: 'linear-gradient(90deg, #8A2BE2, #00E5FF)' }}
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: 2.2, ease: 'easeInOut', delay: 0.6 }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
